import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Folder, FolderOpen, ChevronRight, Home, RefreshCw,
  Upload, FolderPlus, FilePlus, Trash2, Pencil,
  Download, Copy, Eye, AlertCircle,
  Search, X, CheckSquare, Square,
  MoreHorizontal, Archive,
} from 'lucide-react'
import { get, post, put, del, upload as uploadFile, getBaseUrl } from '../utils/api'
import { getFileIcon } from '../utils/fileIcons'
import { toast } from 'sonner'
import ExtractDialog from './ExtractDialog'

function fmt(bytes) {
  if (bytes === 0 || bytes == null) return '—'
  if (bytes < 1024)       return bytes + ' B'
  if (bytes < 1048576)    return (bytes / 1024).toFixed(1) + ' KB'
  if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB'
  return (bytes / 1073741824).toFixed(2) + ' GB'
}

function fmtDate(val) {
  if (!val) return '—'
  const d = typeof val === 'number' ? new Date(val * 1000) : new Date(val)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/* ─── Context Menu ─── */
function CtxMenu({ x, y, items, onClose }) {
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (!ref.current?.contains(e.target)) onClose() }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const left = Math.min(x, window.innerWidth  - 180)
  const top  = Math.min(y, window.innerHeight - items.length * 36 - 16)

  return (
    <div
      ref={ref}
      style={{ left, top }}
      className="fixed z-50 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl py-1 overflow-hidden"
    >
      {items.map((item, i) =>
        item === '---' ? (
          <div key={i} className="my-1 border-t border-gray-100 dark:border-gray-700" />
        ) : (
          <button
            type="button"
            key={i}
            onClick={() => { item.action(); onClose() }}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left ${
              item.danger
                ? 'text-red-600 dark:text-red-400 hover:text-red-700'
                : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <span className="shrink-0">{item.icon}</span>
            {item.label}
          </button>
        )
      )}
    </div>
  )
}

/* ─── Rename inline input ─── */
function RenameInput({ name, onConfirm, onCancel }) {
  const [val, setVal] = useState(name)
  const ref = useRef(null)
  useEffect(() => { ref.current?.select() }, [])

  return (
    <input
      ref={ref}
      value={val}
      onChange={e => setVal(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.stopPropagation(); onConfirm(val) }
        if (e.key === 'Escape') { e.stopPropagation(); onCancel() }
      }}
      onBlur={() => onConfirm(val)}
      onClick={e => e.stopPropagation()}
      className="bg-white dark:bg-gray-700 border border-primary/40 rounded-md px-2 py-0.5 text-xs text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-primary/20 w-full max-w-[200px]"
    />
  )
}

/* ─── Upload progress overlay ─── */
function UploadProgress({ progress, filename }) {
  return (
    <div className="fixed bottom-6 right-6 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl p-4 w-72">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-600 dark:text-gray-300 truncate pr-2">{filename}</span>
        <span className="text-xs text-gray-400">{progress}%</span>
      </div>
      <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}

/* ─── New Folder dialog ─── */
function NewFolderDialog({ onCreate, onClose }) {
  const [name, setName] = useState('new-folder')
  const ref = useRef(null)
  useEffect(() => { ref.current?.select() }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-5 w-80">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">New Folder</p>
        <input
          ref={ref}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') onCreate(name); if (e.key === 'Escape') onClose() }}
          placeholder="Folder name"
          className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/10 mb-4"
        />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-xl text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">Cancel</button>
          <button
            type="button"
            onClick={() => onCreate(name.trim())}
            disabled={!name.trim()}
            className="px-4 py-1.5 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-40 text-white text-sm transition-colors"
          >Create</button>
        </div>
      </div>
    </div>
  )
}

/* ─── New File dialog ─── */
function NewFileDialog({ onCreate, onClose }) {
  const [name, setName] = useState('new-file.html')
  const ref = useRef(null)
  useEffect(() => { ref.current?.select() }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-5 w-80">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">New File</p>
        <input
          ref={ref}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') onCreate(name); if (e.key === 'Escape') onClose() }}
          placeholder="File name"
          className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/10 mb-4"
        />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-xl text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">Cancel</button>
          <button
            type="button"
            onClick={() => onCreate(name.trim())}
            disabled={!name.trim()}
            className="px-4 py-1.5 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-40 text-white text-sm transition-colors"
          >Create</button>
        </div>
      </div>
    </div>
  )
}

/* ─── Delete confirm ─── */
function DeleteConfirm({ items, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-5 w-80">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Delete {items.length > 1 ? `${items.length} items` : `"${items[0]?.name}"`}?
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">This cannot be undone.</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-xl text-gray-500 hover:text-gray-700 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">Cancel</button>
          <button type="button" onClick={onConfirm} className="px-4 py-1.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm transition-colors">Delete</button>
        </div>
      </div>
    </div>
  )
}

/* ─── Main FileBrowser ─── */
export default function FileBrowser({ domain, onOpenFile, activeFile, path = '/', onNavigate }) {
  const [entries, setEntries]     = useState([])
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState(null)
  const [search, setSearch]       = useState('')
  const [sortBy, setSortBy]       = useState('name')
  const [sortDir, setSortDir]     = useState('asc')
  const [selected, setSelected]   = useState(new Set())
  const [renaming, setRenaming]   = useState(null)
  const [ctx, setCtx]             = useState(null)
  const [newFolder, setNewFolder] = useState(false)
  const [newFile, setNewFile]     = useState(false)
  const [extractTarget, setExtractTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [upload, setUpload]       = useState(null)
  const uploadRef = useRef(null)

  const load = useCallback(async (p = path) => {
    setLoading(true)
    setError(null)
    setSelected(new Set())
    try {
      const r = await get(`/websites/${domain}/files`, { path: p })
      setEntries(r?.data?.entries ?? r?.data ?? r ?? [])
    } catch (err) {
      setError(err?.message ?? 'Failed to load directory')
    } finally {
      setLoading(false)
    }
  }, [domain, path])

  useEffect(() => { if (domain) load(path) }, [domain, path])

  const navigate = (newPath) => {
    onNavigate?.(newPath)
    setSearch('')
    setSelected(new Set())
    onOpenFile(null)
  }

  const crumbs = path.split('/').filter(Boolean)

  const processed = [...entries]
    .filter(e => !search || e.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (a.type === 'directory' && b.type !== 'directory') return -1
      if (b.type === 'directory' && a.type !== 'directory') return 1
      let cmp = 0
      if (sortBy === 'name')     cmp = a.name.localeCompare(b.name)
      if (sortBy === 'size')     cmp = (a.size ?? 0) - (b.size ?? 0)
      if (sortBy === 'modified') cmp = (a.modified_at ?? '').localeCompare(b.modified_at ?? '')
      return sortDir === 'asc' ? cmp : -cmp
    })

  const toggleSelect = (e, name) => {
    e.stopPropagation()
    setSelected(prev => {
      const n = new Set(prev)
      n.has(name) ? n.delete(name) : n.add(name)
      return n
    })
  }
  const doRename = async (entry, newName) => {
    setRenaming(null)
    if (!newName || newName === entry.name) return
    const newPath = path.replace(/\/$/, '') + '/' + newName
    try {
      await post(`/websites/${domain}/files/rename`, { from: entry.path, to: newPath })
      toast.success('Renamed')
      load()
    } catch { toast.error('Rename failed') }
  }

  const doDelete = async (items) => {
    setDeleteTarget(null)
    try {
      await Promise.all(items.map(e => del(`/websites/${domain}/files`, { path: e.path })))
      toast.success(items.length > 1 ? `Deleted ${items.length} items` : `Deleted "${items[0].name}"`)
      load()
      if (items.some(e => e.path === activeFile?.path)) onOpenFile(null)
    } catch { toast.error('Delete failed') }
  }

  const doMkdir = async (name) => {
    setNewFolder(false)
    if (!name) return
    try {
      await post(`/websites/${domain}/files/mkdir`, { path: path.replace(/\/$/, '') + '/' + name })
      toast.success('Folder created')
      load()
    } catch { toast.error('Failed to create folder') }
  }

  const doCreateFile = async (name) => {
    setNewFile(false)
    if (!name) return
    const newPath = path.replace(/\/$/, '') + '/' + name
    try {
      await put(`/websites/${domain}/files/write`, { path: newPath, content: '' })
      toast.success('File created')
      await load()
      onOpenFile({ path: newPath, name, type: 'file', size: 0 })
    } catch { toast.error('Failed to create file') }
  }

  const doCopy = async (entry) => {
    try {
      const dest = path.replace(/\/$/, '') + '/' + entry.name.replace(/(\.[^.]+)?$/, m => `-copy${m}`)
      await post(`/websites/${domain}/files/copy`, { from: entry.path, to: dest })
      toast.success('Copied')
      load()
    } catch { toast.error('Copy failed') }
  }

  const isExtractable = (name) => {
    const lower = (name ?? '').toLowerCase()
    return lower.endsWith('.zip') || lower.endsWith('.tar.gz') || lower.endsWith('.tgz')
      || lower.endsWith('.tar.bz2') || lower.endsWith('.tar')
  }

  const suggestExtractDest = (entry) => {
    const parent = entry.path.split('/').slice(0, -1).join('/') || '/'
    const folder = entry.name.replace(/\.(tar\.gz|tar\.bz2|zip|tgz|tar)$/i, '')
    return `${parent === '/' ? '' : parent}/${folder}`
  }

  const doExtract = async (destination) => {
    try {
      const res = await post(`/websites/${domain}/files/extract`, { archive: extractTarget.path, destination })
      if (res.success) {
        toast.success(`Extracted to ${destination}`)
        setExtractTarget(null)
        navigate(destination)
      } else {
        toast.error(res.error?.message || res.message || 'Extraction failed')
      }
    } catch (err) {
      toast.error(err?.error?.message || err?.message || 'Extraction failed')
    }
  }

  const doDownload = (entry) => {
    const token = localStorage.getItem('fm_token') || localStorage.getItem('panel_jwt')
    const base  = getBaseUrl();
    const url   = `${base}/websites/${encodeURIComponent(domain)}/files/download?path=${encodeURIComponent(entry.path)}&token=${token}`
    const a = document.createElement('a')
    a.href = url; a.download = entry.name; a.click()
  }

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files ?? [])
    if (!files.length) return
    const form = new FormData()
    form.append('path', path)
    files.forEach(f => form.append('files[]', f))
    setUpload({ filename: files.length > 1 ? `${files.length} files` : files[0].name, progress: 0 })
    try {
      await uploadFile(`/websites/${domain}/files/upload`, form, (evt) => {
        const pct = Math.round((evt.loaded / evt.total) * 100)
        setUpload(prev => ({ ...prev, progress: pct }))
      })
      toast.success(`Uploaded ${files.length} file(s)`)
      load()
    } catch { toast.error('Upload failed') }
    finally { setUpload(null) }
    e.target.value = ''
  }

  const openCtx = (e, entry) => {
    e.preventDefault()
    e.stopPropagation()
    const isDir = entry.type === 'directory'
    setCtx({
      x: e.clientX, y: e.clientY,
      items: [
        isDir
          ? { icon: <FolderOpen size={13}/>, label: 'Open', action: () => navigate(entry.path) }
          : { icon: <Eye size={13}/>, label: 'Open', action: () => onOpenFile(entry) },
        { icon: <Pencil size={13}/>, label: 'Rename', action: () => setRenaming(entry.path) },
        '---',
        { icon: <Copy size={13}/>, label: 'Duplicate', action: () => doCopy(entry) },
        { icon: <Download size={13}/>, label: 'Download', action: () => doDownload(entry) },
        ...(isExtractable(entry.name) ? [{ icon: <Archive size={13}/>, label: 'Extract…', action: () => setExtractTarget(entry) }] : []),
        '---',
        { icon: <Trash2 size={13}/>, label: 'Delete', danger: true, action: () => setDeleteTarget([entry]) },
      ]
    })
  }

  const clickEntry = (entry) => {
    if (entry.type === 'directory') { navigate(entry.path); return }
    onOpenFile(entry)
  }

  const selectedEntries = processed.filter(e => selected.has(e.name))

  return (
    <div className="flex flex-col h-full select-none">

      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 shrink-0 bg-white dark:bg-gray-800">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 flex-1 min-w-0 overflow-hidden">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors shrink-0"
          >
            <Home size={13} />
          </button>
          {crumbs.map((c, i) => {
            const to = '/' + crumbs.slice(0, i + 1).join('/')
            return (
              <React.Fragment key={to}>
                <ChevronRight size={11} className="text-gray-300 dark:text-gray-600 shrink-0" />
                <button
                  type="button"
                  onClick={() => navigate(to)}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors truncate max-w-[100px]"
                >
                  {c}
                </button>
              </React.Fragment>
            )
          })}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1 shrink-0">
          {selected.size > 0 && (
            <button
              type="button"
              onClick={() => setDeleteTarget(selectedEntries)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs transition-colors"
            >
              <Trash2 size={12} /> Delete ({selected.size})
            </button>
          )}
          <button
            type="button"
            onClick={() => setNewFile(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-medium transition-colors"
          >
            <FilePlus size={13} /> New File
          </button>
          <button
            type="button"
            onClick={() => setNewFolder(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-medium transition-colors"
          >
            <FolderPlus size={13} /> New Folder
          </button>
          <button
            type="button"
            onClick={() => uploadRef.current?.click()}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-medium transition-colors"
          >
            <Upload size={13} /> Upload
          </button>
          <input ref={uploadRef} type="file" multiple className="hidden" onChange={handleUpload} />
          <button
            type="button"
            onClick={() => load()}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-medium transition-colors"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700/50 shrink-0 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 h-7 border border-gray-200 dark:border-gray-600 focus-within:border-primary/40 transition-colors">
          <Search size={11} className="text-gray-400 shrink-0" />
          <input
            type="text" value={search} placeholder="Filter files…"
            onChange={e => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-xs text-gray-700 dark:text-gray-300 placeholder:text-gray-400 outline-none"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
              <X size={11} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto bg-white">
        {loading ? (
          <div className="p-3 grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-1.5">
            {Array.from({ length: 24 }).map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-2 p-3 rounded-xl animate-pulse">
                <div className="w-7 h-7 bg-gray-200 rounded-lg" />
                <div className="h-2 bg-gray-200 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
            <AlertCircle size={24} />
            <p className="text-sm">{error}</p>
            <button type="button" onClick={() => load()} className="text-xs text-primary hover:text-primary/80">Retry</button>
          </div>
        ) : processed.length === 0 ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-xs">
            {search ? 'No matches' : 'Empty folder'}
          </div>
        ) : (
          <div className="p-3 grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-1.5">
            {processed.map((entry) => {
              const isDir    = entry.type === 'directory'
              const { icon, color } = getFileIcon(entry.name)
              const isSel    = selected.has(entry.name)
              const isActive = activeFile?.path === entry.path
              const isRen    = renaming === entry.path

              return (
                <div
                  key={entry.path}
                  onClick={() => clickEntry(entry)}
                  onContextMenu={e => openCtx(e, entry)}
                  className={`group relative flex flex-col items-center gap-1.5 p-3 rounded-xl cursor-pointer transition-colors ${
                    isActive
                      ? 'bg-primary/10'
                      : isSel
                        ? 'bg-gray-100'
                        : 'hover:bg-gray-50'
                  }`}
                >
                  <button
                    type="button"
                    onClick={e => toggleSelect(e, entry.name)}
                    className={`absolute top-1.5 left-1.5 ${isSel ? 'opacity-100 text-primary' : 'opacity-0 group-hover:opacity-100 text-gray-400'} transition-opacity`}
                  >
                    {isSel ? <CheckSquare size={12} /> : <Square size={12} />}
                  </button>
                  <span className={`${isDir ? 'text-amber-500' : color}`}>
                    {isDir ? <Folder size={28} /> : React.cloneElement(icon, { size: 28 })}
                  </span>
                  {isRen ? (
                    <RenameInput
                      name={entry.name}
                      onConfirm={n => doRename(entry, n)}
                      onCancel={() => setRenaming(null)}
                    />
                  ) : (
                    <span className={`text-[10.5px] text-center truncate w-full leading-tight ${
                      isActive ? 'text-primary font-medium' : 'text-gray-600'
                    }`}>
                      {entry.name}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Status bar */}
      <div className="shrink-0 px-4 py-1.5 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 flex items-center justify-between">
        <span className="text-[10px] text-gray-400 dark:text-gray-500">
          {processed.length} item{processed.length !== 1 ? 's' : ''}
          {selected.size > 0 ? `, ${selected.size} selected` : ''}
        </span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono truncate max-w-[200px]">{path}</span>
      </div>

      {/* Dialogs */}
      {ctx && <CtxMenu x={ctx.x} y={ctx.y} items={ctx.items} onClose={() => setCtx(null)} />}
      {newFolder && <NewFolderDialog onCreate={doMkdir} onClose={() => setNewFolder(false)} />}
      {newFile && <NewFileDialog onCreate={doCreateFile} onClose={() => setNewFile(false)} />}
      {extractTarget && (
        <ExtractDialog
          archiveName={extractTarget.name}
          suggestedDest={suggestExtractDest(extractTarget)}
          onExtract={doExtract}
          onClose={() => setExtractTarget(null)}
        />
      )}
      {deleteTarget && (
        <DeleteConfirm
          items={deleteTarget}
          onConfirm={() => doDelete(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
        />
      )}
      {upload && <UploadProgress filename={upload.filename} progress={upload.progress} />}
    </div>
  )
}
